import { getConfiguration } from '../../../services/configurationManager';
import { parseHeartRateData } from '../../../utils/hrDataRepair';
import { buildAnalysisPrompt } from './buildAnalysisPrompt';
import { callOpenAIRepair } from './client';
import {
  countExpectedGapInsertions,
  detectHrDataIssues,
  hasRepairableIssues,
} from './detectHrDataIssues';
import { parseAnalysisResponse } from './parseAnalysisResponse';
import type { SessionHrDataAnalysisResult } from './types';

const sessionHrDataAnalysisCache = new Map<
  number,
  SessionHrDataAnalysisResult
>();
const sessionHrDataAnalysisPending = new Map<
  number,
  Promise<SessionHrDataAnalysisResult>
>();

export const getCachedSessionHrDataAnalysis = (
  sessionId: number
): SessionHrDataAnalysisResult | null =>
  sessionHrDataAnalysisCache.get(sessionId) ?? null;

export const invalidateSessionHrDataAnalysisCache = (sessionId?: number) => {
  if (typeof sessionId === 'number') {
    sessionHrDataAnalysisCache.delete(sessionId);
    sessionHrDataAnalysisPending.delete(sessionId);
    return;
  }

  sessionHrDataAnalysisCache.clear();
  sessionHrDataAnalysisPending.clear();
};

export const analyzeSessionHrDataEvents = async (
  events: ReturnType<typeof parseHeartRateData>
): Promise<SessionHrDataAnalysisResult> => {
  const issues = detectHrDataIssues(events);

  if (!hasRepairableIssues(issues)) {
    return {
      status: 'clean',
      summary: 'Nessun duplicato nello stesso secondo né gap rilevati.',
      findings: [],
      duplicateCount: 0,
      gapCount: 0,
      gapInsertionCount: 0,
    };
  }

  const duplicateCount = issues.duplicates.length;
  const gapCount = issues.gaps.length;
  const gapInsertionCount = countExpectedGapInsertions(issues);

  try {
    const messages = buildAnalysisPrompt(issues);
    const { content } = await callOpenAIRepair(messages);
    const parsed = parseAnalysisResponse(content);

    return {
      status: 'needs_repair',
      summary: parsed.summary,
      findings:
        parsed.findings.length > 0
          ? parsed.findings
          : [
              duplicateCount > 0
                ? `${duplicateCount} secondi con eventi duplicati.`
                : null,
              gapCount > 0
                ? `${gapCount} gap con ${gapInsertionCount} secondi mancanti.`
                : null,
            ].filter((item): item is string => Boolean(item)),
      duplicateCount,
      gapCount,
      gapInsertionCount,
    };
  } catch (error) {
    return {
      status: 'failed',
      message:
        error instanceof Error
          ? error.message
          : 'Analisi sessione non riuscita.',
      duplicateCount,
      gapCount,
      gapInsertionCount,
    };
  }
};

export const analyzeSessionHrData = async (
  sessionId: number
): Promise<SessionHrDataAnalysisResult> => {
  const cached = sessionHrDataAnalysisCache.get(sessionId);
  if (cached) {
    return cached;
  }

  const pending = sessionHrDataAnalysisPending.get(sessionId);
  if (pending) {
    return pending;
  }

  const promise = (async () => {
    const configuration = await getConfiguration(sessionId);
    const originalEvents = parseHeartRateData(configuration.data.hrData);
    const result = await analyzeSessionHrDataEvents(originalEvents);

    if (result.status === 'clean' || result.status === 'needs_repair') {
      sessionHrDataAnalysisCache.set(sessionId, result);
    }

    return result;
  })();

  sessionHrDataAnalysisPending.set(sessionId, promise);

  try {
    return await promise;
  } finally {
    sessionHrDataAnalysisPending.delete(sessionId);
  }
};
